// components/NutritionLanding.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { ScrollArea } from "./ui/scroll-area";
import { ChevronRight, Calculator, Book, Scale, Brain } from 'lucide-react';

const NutritionLanding = () => {
  const navigate = useNavigate();
  
  return (
    <div className="container mx-auto p-4 max-w-4xl space-y-6">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold">NutriNexus</h1>
        <p className="text-xl text-muted-foreground">
          Calculate your personalized nutrition needs for cutting, maintaining, or bulking
        </p>
        <Button onClick={() => navigate('/calculator')} size="lg" className="mt-4">
          Get Started <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-8">

      <Card>
        <CardHeader>
          <Calculator className="h-6 w-6 mb-2" />
          <CardTitle>What is TDEE?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <p className="mb-2">TDEE (Total Daily Energy Expenditure) represents your total daily caloric needs. It consists of:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>BMR (Basal Metabolic Rate) - calories burned at rest for basic life functions</li>
                <li>Daily physical activity level - from work and general movement</li>
                <li>Exercise and movement - calories burned during workouts</li>
                <li>Thermic effect of food - energy used to digest food (about 10% of intake)</li>
              </ul>
            </div>

            <div className="mt-4 pt-4 border-t">
              <h3 className="font-semibold mb-2">Why Calculate TDEE?</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Establishes your maintenance calorie baseline</li>
                <li>Helps determine surplus/deficit for weight goals</li>
                <li>Accounts for individual activity differences</li>
                <li>Provides foundation for macro calculations</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <Brain className="h-6 w-6 mb-2" />
          <CardTitle>Calculator Features</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">Core Calculations</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>BMR using the Mifflin-St Jeor equation</li>
                <li>Activity level multipliers for accuracy</li>
                <li>Lean body mass estimation</li>
                <li>Customizable calorie surplus/deficit</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Macro Customization</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Adjustable protein ratios for cutting/bulking</li>
                <li>Flexible fat intake settings</li>
                <li>Automatic carb balancing</li>
                <li>Visual macro distribution charts</li>
              </ul>
            </div>

            <div className="mt-4 pt-4 border-t">
              <h3 className="font-semibold mb-2">Additional Features</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Imperial/Metric unit toggle</li>
                <li>Dark/Light mode support</li>
                <li>Mobile-friendly design</li>
                <li>Clear visual data presentation</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

        <Card>
          <CardHeader>
            <Scale className="h-6 w-6 mb-2" />
            <CardTitle>Understanding Macros</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Protein (4 calories/gram)</h3>
                <ul className="list-disc list-inside space-y-1">
                  <li>Essential for muscle repair, growth, and maintenance</li>
                  <li>Helps preserve muscle mass during weight loss</li>
                  <li>Increased needs during resistance training</li>
                  <li>Common sources: meat, fish, eggs, dairy, legumes</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Carbohydrates (4 calories/gram)</h3>
                <ul className="list-disc list-inside space-y-1">
                  <li>Primary energy source for high-intensity activities</li>
                  <li>Important for muscle glycogen replenishment</li>
                  <li>Can be adjusted based on activity level and goals</li>
                  <li>Sources: rice, potatoes, fruits, vegetables, grains</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Fats (9 calories/gram)</h3>
                <ul className="list-disc list-inside space-y-1">
                  <li>Critical for hormone production and regulation</li>
                  <li>Helps with vitamin absorption (A, D, E, K)</li>
                  <li>Provides essential fatty acids</li>
                  <li>Sources: oils, nuts, avocados, fatty fish</li>
                </ul>
              </div>

              <div className="mt-4 pt-4 border-t">
                <h3 className="font-semibold mb-2">Macro Adjustments</h3>
                <p className="text-sm text-muted-foreground">
                  This calculator helps balance your macros based on your goals:
                </p>
                <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                  <li>Cutting: Higher protein, moderate fat, lower carbs</li>
                  <li>Bulking: Moderate protein, moderate fat, higher carbs</li>
                  <li>Maintenance: Balanced distribution of all macros</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <Book className="h-6 w-6 mb-2" />
            <CardTitle>Sources & References</CardTitle>
          </CardHeader>
          <CardContent>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline">View Sources</Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>Sources & References</DialogTitle>
                </DialogHeader>
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-4 p-4">
                    <div>
                      <h3 className="font-semibold mb-2">Primary Research</h3>
                      <p className="mb-2">BMR Calculation (Mifflin-St Jeor Equation):</p>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>Male: 10 × weight(kg) + 6.25 × height(cm) - 5 × age + 5</li>
                        <li>Female: 10 × weight(kg) + 6.25 × height(cm) - 5 × age - 161</li>
                      </ul>
                      <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5074671/" className="text-blue-500 hover:underline block mt-2" target="_blank" rel="noopener noreferrer">
                        NCBI - Mifflin-St. Jeor Equation Study
                      </a>
                    </div>

                    <div>
                      <h3 className="font-semibold mb-2">Expert Guidance</h3>
                      <p>Dr. Mike Israetel - Renaissance Periodization</p>
                      <a href="https://youtu.be/aJFiGC13xIw" className="text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer">
                        Watch Video Guide
                      </a>
                    </div>

                    <div>
                      <h3 className="font-semibold mb-2">Macro Guidelines</h3>
                      <div className="space-y-2">
                        <a href="https://www.reddit.com/r/Fitness/comments/30hymm/im_bulking_for_the_first_time_and_i_need_advice/cpsl78d/" className="text-blue-500 hover:underline block" target="_blank" rel="noopener noreferrer">
                          Bulking Macro Guidelines
                        </a>
                        <a href="https://www.reddit.com/r/Fitness/comments/31n9yy/trying_to_cut/cq35dm0/" className="text-blue-500 hover:underline block" target="_blank" rel="noopener noreferrer">
                          Cutting Macro Guidelines
                        </a>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold mb-2">Additional Resources</h3>
                      <ul className="space-y-2">
                        <li>
                          <a href="https://www.k-state.edu/paccats/Contents/PA/PDF/Physical%20Activity%20and%20Controlling%20Weight.pdf" className="text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer">
                            Activity Level Classifications - KSU Research
                          </a>
                        </li>
                        <li>
                          <a href="https://www.thestrength.co/the-vertical-diet-in-10-minutes/" className="text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer">
                            Vertical Diet Overview
                          </a>
                        </li>
                        <li>
                          <a href="https://www.calculator.net/lean-body-mass-calculator.html" className="text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer">
                            Lean Body Mass Calculations
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </ScrollArea>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NutritionLanding;